import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiGet } from "../services/api";
import { useAuth } from "./AuthContext";

// Types matching backend definitions
export type Role = "super_admin" | "admin" | "manager" | "staff" | "cashier";
export type Module =
  | "dashboard"
  | "inventory"
  | "sales-orders"
  | "sales-invoices"
  | "credit-notes"
  | "customers"
  | "suppliers"
  | "purchases"
  | "purchase-returns"
  | "payments"
  | "reports"
  | "expenses"
  | "assets"
  | "settings"
  | "user-management"
  | "audit-log"
  | "categories";

export type PermissionMatrix = Record<Role, Module[]>;

export interface PermissionContextValue {
  permissions: string[];
  allPermissions: PermissionMatrix | null;
  hasAccess: (module: string) => boolean;
  isLoading: boolean;
  error: string | null;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user, role } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionMatrix | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // If no authenticated user or no role, reset state
    if (!user || !role) {
      setPermissions([]);
      setAllPermissions(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Bypass settings/permissions loading for super_admin
    if (role === ("super_admin" as any)) {
      const allModules = [
        "dashboard", "inventory", "sales-orders", "sales-invoices",
        "credit-notes", "customers", "suppliers", "purchases",
        "purchase-returns", "payments", "reports", "expenses",
        "assets", "settings", "user-management", "audit-log", "categories"
      ];
      setPermissions(allModules);
      setAllPermissions(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    async function loadPermissions() {
      setIsLoading(true);
      try {
        const res = await apiGet<{ permissions?: PermissionMatrix; data?: PermissionMatrix } | PermissionMatrix>("/api/settings/permissions");
        const data = (res as { permissions?: PermissionMatrix }).permissions || (res as { data?: PermissionMatrix }).data || (res as PermissionMatrix);
        if (!cancelled && data) {
          const matrix: Partial<PermissionMatrix> = {};
          const roles: Role[] = ["admin", "manager", "staff", "cashier"];

          for (const r of roles) {
            if (Array.isArray(data[r])) {
              matrix[r] = data[r] as Module[];
            } else {
              matrix[r] = [];
            }
          }

          const fullMatrix = matrix as PermissionMatrix;
          setAllPermissions(fullMatrix);
          setPermissions(role ? fullMatrix[role] ?? [] : []);
          setError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const apiErr = err as { message?: string };
          setError(apiErr.message || "Failed to load permissions");
          setPermissions([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadPermissions();
    return () => { cancelled = true; };
  }, [user, role]);

  const hasAccess = useCallback(
    (module: string): boolean => {
      return permissions.includes(module);
    },
    [permissions],
  );

  return (
    <PermissionContext.Provider
      value={{ permissions, allPermissions, hasAccess, isLoading, error }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionContextValue {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error(
      "usePermissions must be used within a PermissionProvider",
    );
  }
  return ctx;
}
